import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { PageHeader } from "../components/PageHeader";
import { InlineError, LoadingState } from "../components/States";
import { useToast } from "../components/Toast";
import { adminApi } from "../lib/api";
import { enforceAtalayBrand, isPlainObject, stableJson } from "../lib/content";
import {
  activeMailboxDestination,
  isVerifiedMailboxDestination,
  mailboxRoutingState,
  normalizeMailboxDestination,
  pendingMailboxDestination,
  unchangedMailboxAction,
  validateMailboxDestination
} from "../lib/mailbox";
import type {
  JsonObject,
  JsonValue,
  MailboxForwardingSettings
} from "../types";

interface SiteSettings {
  contactEmail: string;
  address: string;
  domain: string;
  defaultLocale: "en" | "tr";
}

const DEFAULT_SETTINGS: SiteSettings = {
  contactEmail: "info@atalayllc.com",
  address: "19 MAYIS MAH. HALASKARGAZI CAD. YAPI APT NO 220/14",
  domain: "atalayllc.com",
  defaultLocale: "en"
};

function localizedString(value: JsonValue | undefined, fallback: string): string {
  if (typeof value === "string") return value;
  if (!isPlainObject(value)) return fallback;
  if (typeof value.en === "string") return value.en;
  if (typeof value.tr === "string") return value.tr;
  return fallback;
}

function settingsFromDraft(draft: JsonValue): SiteSettings {
  const root = isPlainObject(draft) ? draft : {};
  const config = isPlainObject(root.siteSettings) ? root.siteSettings : {};
  const shared = isPlainObject(root.shared) ? root.shared : {};
  const business = isPlainObject(shared.business) ? shared.business : {};
  return {
    contactEmail: localizedString(
      business.email,
      DEFAULT_SETTINGS.contactEmail
    ),
    address: localizedString(business.address, DEFAULT_SETTINGS.address),
    domain:
      typeof config.domain === "string"
        ? config.domain
        : DEFAULT_SETTINGS.domain,
    defaultLocale: config.defaultLocale === "tr" ? "tr" : "en"
  };
}

function draftWithSettings(draft: JsonValue, settings: SiteSettings): JsonValue {
  const root: JsonObject = isPlainObject(draft) ? draft : {};
  const existingConfig = isPlainObject(root.siteSettings)
    ? root.siteSettings
    : {};
  const shared = isPlainObject(root.shared) ? root.shared : {};
  const business = isPlainObject(shared.business) ? shared.business : {};
  return enforceAtalayBrand({
    ...root,
    shared: {
      ...shared,
      business: {
        ...business,
        email: {
          en: settings.contactEmail,
          tr: settings.contactEmail
        },
        address: {
          en: settings.address,
          tr: settings.address
        }
      }
    },
    siteSettings: {
      ...existingConfig,
      domain: settings.domain,
      defaultLocale: settings.defaultLocale,
      brand: "ATALAY"
    }
  });
}

function formatSettingsTimestamp(value?: string): string {
  if (!value || !Number.isFinite(Date.parse(value))) return "";
  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

export function SettingsPage() {
  const { showToast } = useToast();
  const [draft, setDraft] = useState<JsonValue | null>(null);
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [savedSettings, setSavedSettings] =
    useState<SiteSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [destinationEmail, setDestinationEmail] = useState("");
  const [savedDestinationEmail, setSavedDestinationEmail] = useState("");
  const [mailbox, setMailbox] = useState<MailboxForwardingSettings | null>(null);
  const [mailboxLoading, setMailboxLoading] = useState(true);
  const [mailboxSaving, setMailboxSaving] = useState(false);
  const [mailboxLoadError, setMailboxLoadError] = useState("");
  const [mailboxSaveError, setMailboxSaveError] = useState("");
  const [mailboxValidation, setMailboxValidation] = useState("");
  const [mailboxSuccess, setMailboxSuccess] = useState("");

  const changed = useMemo(
    () => stableJson(settings as unknown as JsonValue) !== stableJson(savedSettings as unknown as JsonValue),
    [settings, savedSettings]
  );
  const destinationChanged = destinationEmail !== savedDestinationEmail;
  const routingState = mailbox ? mailboxRoutingState(mailbox) : "inactive";
  const activeDestination = mailbox ? activeMailboxDestination(mailbox) : "";
  const pendingDestination = mailbox ? pendingMailboxDestination(mailbox) : "";
  const normalizedDestination = normalizeMailboxDestination(destinationEmail);
  const unchangedAction =
    !destinationChanged && mailbox
      ? unchangedMailboxAction(mailbox, normalizedDestination)
      : null;
  const checkingPending = unchangedAction === "check_verification";
  const activatingUnchanged = unchangedAction === "activate";
  const forwardingAddressNormalized = normalizeMailboxDestination(
    mailbox?.forwardingAddress || ""
  ).toLowerCase();
  const verifiedDestinations = (mailbox?.destinations || []).filter(
    (destination) =>
      destination.verified &&
      normalizeMailboxDestination(destination.email).toLowerCase() !==
        forwardingAddressNormalized
  );
  const selectedDestinationVerified =
    mailbox &&
    normalizedDestination.toLowerCase() !== forwardingAddressNormalized
      ? isVerifiedMailboxDestination(mailbox, normalizedDestination)
    : false;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await adminApi.content();
      const source = response.draft ?? response.published ?? {};
      const nextSettings = settingsFromDraft(source);
      setDraft(source);
      setSettings(nextSettings);
      setSavedSettings(nextSettings);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Website settings could not be loaded."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMailboxSettings = useCallback(async () => {
    setMailboxLoading(true);
    setMailboxLoadError("");
    setMailboxSaveError("");
    setMailboxValidation("");
    setMailboxSuccess("");
    try {
      const response = await adminApi.mailboxForwardingSettings();
      if (typeof response.forwardingAddress !== "string") {
        throw new Error("The API did not return the public mailbox address.");
      }
      const selected =
        pendingMailboxDestination(response) ||
        activeMailboxDestination(response) ||
        normalizeMailboxDestination(response.destinationEmail || "");
      setDestinationEmail(selected);
      setSavedDestinationEmail(selected);
      setMailbox(response);
    } catch (requestError) {
      setMailboxLoadError(
        requestError instanceof Error
          ? requestError.message
          : "Mailbox forwarding settings could not be loaded."
      );
    } finally {
      setMailboxLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadMailboxSettings();
  }, [loadMailboxSettings]);

  useEffect(() => {
    const hasUnsavedSettings = changed || destinationChanged;
    window.dispatchEvent(
      new CustomEvent("atalay:dirty-state", { detail: hasUnsavedSettings })
    );
    function beforeUnload(event: BeforeUnloadEvent) {
      if (!hasUnsavedSettings) return;
      event.preventDefault();
    }
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [changed, destinationChanged]);

  useEffect(
    () => () => {
      window.dispatchEvent(
        new CustomEvent("atalay:dirty-state", { detail: false })
      );
    },
    []
  );

  function update<Key extends keyof SiteSettings>(
    key: Key,
    value: SiteSettings[Key]
  ) {
    setSettings((current) => ({ ...current, [key]: value }));
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (draft === null) return;
    setSaving(true);
    try {
      const nextDraft = draftWithSettings(draft, {
        ...settings,
        contactEmail: settings.contactEmail.trim(),
        address: settings.address.trim(),
        domain: settings.domain
          .trim()
          .replace(/^https?:\/\//i, "")
          .replace(/\/+$/, "")
      });
      await adminApi.saveDraft(nextDraft);
      const normalizedSettings = settingsFromDraft(nextDraft);
      setDraft(nextDraft);
      setSettings(normalizedSettings);
      setSavedSettings(normalizedSettings);
      showToast("Website settings saved to the draft.", "success");
    } catch (requestError) {
      showToast(
        requestError instanceof Error
          ? requestError.message
          : "Website settings could not be saved.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  }

  async function saveMailboxForwarding(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalized = normalizeMailboxDestination(destinationEmail);
    const validation = validateMailboxDestination(
      normalized,
      mailbox?.forwardingAddress
    );
    setMailboxValidation(validation);
    setMailboxSaveError("");
    setMailboxSuccess("");
    if (validation) return;

    setMailboxSaving(true);
    try {
      const response = await adminApi.updateMailboxForwarding(normalized);
      const responseState = mailboxRoutingState(response);
      const previousActiveDestination = mailbox
        ? activeMailboxDestination(mailbox)
        : "";
      const merged: MailboxForwardingSettings = {
        ...(mailbox || {
          forwardingAddress: "info@atalayllc.com"
        }),
        ...response,
        destinationEmail: response.destinationEmail || normalized,
        ...(["verification_pending", "activation_ready"].includes(
          responseState
        ) &&
        !pendingMailboxDestination(response)
          ? { pendingDestinationEmail: normalized }
          : {}),
        ...(["verification_pending", "activation_ready"].includes(
          responseState
        ) &&
        previousActiveDestination &&
        !response.activeDestinationEmail
          ? { activeDestinationEmail: previousActiveDestination }
          : {}),
        ...(responseState === "verification_pending"
          ? {
              pendingVerification: true,
              pendingActivation: false,
              status: response.status || "pending_verification"
            }
          : {}),
        ...(responseState === "activation_ready"
          ? {
              pendingVerification: false,
              pendingActivation: true,
              status: response.status || "pending_activation"
            }
          : {}),
        ...(responseState === "active" &&
        !activeMailboxDestination(response)
          ? { activeDestinationEmail: normalized }
          : {}),
        ...(responseState === "active"
          ? {
              pendingVerification: false,
              pendingActivation: false,
              pendingDestinationEmail: undefined,
              requestedDestinationEmail: undefined,
              status: response.status || "active",
              ruleStatus: response.ruleStatus || "active",
              ruleEnabled: true,
              enabled: true,
              configured: true,
              active: true
            }
          : {})
      };
      const nextSelected =
        pendingMailboxDestination(merged) ||
        activeMailboxDestination(merged) ||
        normalized;
      setDestinationEmail(nextSelected);
      setSavedDestinationEmail(nextSelected);
      setMailbox(merged);

      const mergedState = mailboxRoutingState(merged);
      if (mergedState === "verification_pending") {
        showToast(
          "Cloudflare verification is required before routing changes.",
          "info"
        );
      } else if (mergedState === "activation_ready") {
        setMailboxSuccess(
          "Destination verified. Select Activate forwarding to enable the route."
        );
        showToast("Destination verified and ready to activate.", "success");
      } else if (mergedState === "active") {
        setMailboxSuccess(
          `Mailbox forwarding is active to ${activeMailboxDestination(merged) || normalized}.`
        );
        showToast("Mailbox forwarding updated.", "success");
      } else {
        showToast("Mailbox forwarding state refreshed.", "info");
      }
    } catch (requestError) {
      const message =
        requestError instanceof Error
          ? requestError.message
          : "Mailbox forwarding could not be updated.";
      setMailboxSaveError(message);
      showToast(message, "error");
    } finally {
      setMailboxSaving(false);
    }
  }

  function reloadAllSettings() {
    void load();
    void loadMailboxSettings();
  }

  return (
    <>
      <PageHeader
        eyebrow="Core configuration"
        title="Settings"
        description="Control mailbox forwarding, public contact details, domain, and locale preferences."
        actions={
          <button type="button" className="button" onClick={reloadAllSettings}>
            Reload
          </button>
        }
      />

      <div className="settings-stack">
        <section
          className="mailbox-settings panel"
          aria-labelledby="mailbox-settings-title"
        >
          <div className="panel__heading">
            <div>
              <p className="eyebrow">Email routing</p>
              <h2 id="mailbox-settings-title">Mailbox forwarding</h2>
              <p className="mailbox-settings__description">
                Route mail received at the public ATALAY mailbox to a verified
                destination managed through Cloudflare.
              </p>
            </div>
            {!mailboxLoading && !mailboxLoadError && (
              <button
                type="button"
                className="button button--small"
                onClick={() => void loadMailboxSettings()}
              >
                Reload
              </button>
            )}
          </div>

          {mailboxLoading ? (
            <LoadingState label="Loading mailbox forwarding" />
          ) : mailboxLoadError ? (
            <InlineError
              message={mailboxLoadError}
              retry={() => void loadMailboxSettings()}
            />
          ) : mailbox ? (
            <form
              className="mailbox-settings__form"
              onSubmit={saveMailboxForwarding}
              noValidate
            >
              <div className="mailbox-routing-status" aria-live="polite">
                <span
                  className={`status-badge ${
                    routingState === "active"
                      ? "status-badge--success"
                      : routingState === "activation_ready"
                        ? "status-badge--success"
                        : routingState === "verification_pending" ||
                            routingState === "disabled"
                        ? "status-badge--warning"
                        : ""
                  }`}
                >
                  {routingState === "active"
                    ? "Active"
                    : routingState === "activation_ready"
                      ? "Verified · Ready to activate"
                      : routingState === "verification_pending"
                      ? "Verification pending"
                      : routingState === "disabled"
                        ? "Disabled"
                      : "Not active"}
                </span>
                <p>
                  {routingState === "active"
                    ? "Incoming mail is forwarding normally."
                    : routingState === "activation_ready"
                      ? "The saved destination is verified and ready to activate."
                      : routingState === "verification_pending"
                      ? "A destination is waiting for Cloudflare verification."
                      : routingState === "disabled"
                        ? "The mailbox rule exists but forwarding is disabled."
                      : "No mailbox forwarding route is currently active."}
                </p>
              </div>

              <div className="mailbox-route-grid">
                <div className="field">
                  <label htmlFor="public-mailbox">Public mailbox (fixed)</label>
                  <input
                    id="public-mailbox"
                    type="email"
                    readOnly
                    value={mailbox.forwardingAddress}
                  />
                </div>
                <div className="field">
                  <label htmlFor="active-mailbox-destination">
                    Current forwards-to address
                  </label>
                  <input
                    id="active-mailbox-destination"
                    readOnly
                    value={activeDestination || "Not active"}
                  />
                </div>
              </div>

              <div className="field mailbox-destination-field">
                <label htmlFor="mailbox-destination">
                  Forward mail to
                  {selectedDestinationVerified && (
                    <span className="verified-label">Verified</span>
                  )}
                </label>
                <input
                  id="mailbox-destination"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  list="verified-mailbox-destinations"
                  maxLength={90}
                  required
                  value={destinationEmail}
                  aria-invalid={Boolean(mailboxValidation)}
                  aria-describedby={
                    mailboxValidation
                      ? "mailbox-destination-error"
                      : "mailbox-destination-help"
                  }
                  onBlur={() =>
                    setMailboxValidation(
                      validateMailboxDestination(
                        destinationEmail,
                        mailbox.forwardingAddress
                      )
                    )
                  }
                  onChange={(event) => {
                    setDestinationEmail(event.target.value);
                    setMailboxValidation("");
                    setMailboxSaveError("");
                    setMailboxSuccess("");
                  }}
                />
                <datalist id="verified-mailbox-destinations">
                  {verifiedDestinations.map((destination) => (
                    <option
                      value={destination.email}
                      label="Verified"
                      key={destination.email}
                    />
                  ))}
                </datalist>
                <p className="field__help" id="mailbox-destination-help">
                  Choose a verified address or enter a new destination. New
                  addresses receive a verification email from Cloudflare.
                </p>
                {mailboxValidation && (
                  <p
                    className="field__error"
                    id="mailbox-destination-error"
                    role="alert"
                  >
                    {mailboxValidation}
                  </p>
                )}
              </div>

              {verifiedDestinations.length > 0 && (
                <div
                  className="verified-destinations"
                  aria-label="Verified forwarding destinations"
                >
                  <span>Verified destinations</span>
                  <div>
                    {verifiedDestinations.map((destination) => (
                      <button
                        type="button"
                        className="destination-option"
                        key={destination.email}
                        onClick={() => {
                          setDestinationEmail(destination.email);
                          setMailboxValidation("");
                          setMailboxSaveError("");
                          setMailboxSuccess("");
                        }}
                      >
                        {destination.email}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {routingState === "verification_pending" && (
                <div className="mailbox-pending" role="status">
                  <strong>Cloudflare verification required</strong>
                  <p>
                    A verification email was sent to{" "}
                    <b>{pendingDestination || normalizedDestination}</b>. The
                    current route
                    {activeDestination ? ` to ${activeDestination}` : ""} stays
                    unchanged until that address is verified. Complete the
                    verification, then return here and select Check &amp;
                    activate.
                  </p>
                </div>
              )}

              {routingState === "activation_ready" && (
                <div
                  className="mailbox-pending mailbox-pending--ready"
                  role="status"
                >
                  <strong>Destination verified and ready</strong>
                  <p>
                    <b>{pendingDestination || normalizedDestination}</b> has
                    completed Cloudflare verification. Select Activate
                    forwarding to apply the saved route.
                  </p>
                </div>
              )}

              {routingState === "disabled" &&
                selectedDestinationVerified && (
                  <div className="mailbox-pending" role="status">
                    <strong>Forwarding is disabled</strong>
                    <p>
                      The configured destination is verified. Select Activate
                      forwarding to re-enable this mailbox rule.
                    </p>
                  </div>
                )}

              {mailbox.updatedAt && (
                <p className="mailbox-settings__meta">
                  Last updated {formatSettingsTimestamp(mailbox.updatedAt)}
                </p>
              )}

              <div className="mailbox-settings__status" aria-live="polite">
                {mailboxSaveError && (
                  <p className="field__error" role="alert">
                    {mailboxSaveError}
                  </p>
                )}
                {mailboxSuccess && (
                  <p className="success-message">{mailboxSuccess}</p>
                )}
              </div>

              <div className="mailbox-settings__footer">
                <span>
                  {destinationChanged
                    ? "Unsaved forwarding destination"
                    : checkingPending
                      ? "Ready to check verification"
                      : activatingUnchanged &&
                          routingState === "activation_ready"
                        ? "Verified destination is ready to activate"
                        : activatingUnchanged &&
                            routingState === "disabled"
                          ? "Disabled route is ready to re-enable"
                      : routingState === "active"
                        ? "Forwarding route is saved"
                        : "No unsaved changes"}
                </span>
                <button
                  type="submit"
                  className="button button--primary"
                  disabled={
                    mailboxSaving ||
                    (!destinationChanged && !unchangedAction)
                  }
                >
                  {mailboxSaving
                    ? checkingPending
                      ? "Checking…"
                      : activatingUnchanged
                        ? "Activating…"
                      : "Saving…"
                    : checkingPending
                      ? "Check & activate"
                      : activatingUnchanged
                        ? "Activate forwarding"
                      : "Save forwarding"}
                </button>
              </div>
            </form>
          ) : (
            <InlineError message="Mailbox forwarding settings are unavailable." />
          )}
        </section>

        {loading ? (
          <LoadingState label="Loading website settings" />
        ) : error ? (
          <InlineError message={error} retry={() => void load()} />
        ) : (
          <form className="settings-form panel" onSubmit={save}>
            <div className="settings-form__intro">
              <div>
                <p className="eyebrow">Brand</p>
                <h2>ATALAY</h2>
              </div>
              <span className="locked-label">Locked brand</span>
            </div>

            <div className="field">
              <label htmlFor="settings-email">Contact email</label>
              <input
                id="settings-email"
                type="email"
                required
                value={settings.contactEmail}
                onChange={(event) => update("contactEmail", event.target.value)}
              />
              <p className="field__help">
                Used for public contact links and business correspondence.
              </p>
            </div>

            <div className="field">
              <label htmlFor="settings-address">Company address</label>
              <textarea
                id="settings-address"
                rows={4}
                required
                value={settings.address}
                onChange={(event) => update("address", event.target.value)}
              />
            </div>

            <div className="settings-form__row">
              <div className="field">
                <label htmlFor="settings-domain">Primary domain</label>
                <input
                  id="settings-domain"
                  required
                  inputMode="url"
                  value={settings.domain}
                  onChange={(event) => update("domain", event.target.value)}
                  placeholder="atalayllc.com"
                />
                <p className="field__help">Enter the hostname without a path.</p>
              </div>

              <div className="field">
                <label htmlFor="settings-locale">Default locale</label>
                <select
                  id="settings-locale"
                  value={settings.defaultLocale}
                  onChange={(event) =>
                    update(
                      "defaultLocale",
                      event.target.value === "tr" ? "tr" : "en"
                    )
                  }
                >
                  <option value="en">English</option>
                  <option value="tr">Türkçe</option>
                </select>
                <p className="field__help">
                  Used when no language preference is available.
                </p>
              </div>
            </div>

            <div className="settings-form__footer">
              <div>
                <strong>{changed ? "Unsaved changes" : "Settings saved"}</strong>
                <small>
                  Saving updates the draft. Publish it from Content when ready.
                </small>
              </div>
              <button
                type="submit"
                className="button button--primary"
                disabled={!changed || saving}
              >
                {saving ? "Saving…" : "Save settings"}
              </button>
            </div>
          </form>
        )}

      </div>
    </>
  );
}
